/**
 * emailHygieneVerificationEvidence — EMAIL-HYGIENE-RECOVERY-EVIDENCE-001 (Gate D).
 *
 * Operator vocabulary for the backend-derived provider-verification evidence
 * (app/services/email_hygiene_verification_evidence_view.py). Presentation
 * only: this module derives nothing and contains no eligibility logic. The
 * backend remains the sole authority on the decomposition, exactly as
 * EMAIL-HYGIENE-RECON-002 requires — the frontend must never re-derive
 * `absent_no_evidence` vs `absent_divergent` from observed timestamps itself.
 *
 * Why the distinction is surfaced at all: the persisted disposition `absent`
 * covers two materially different provider states. One is safe for a future
 * convergence; the other would revert an edit of unknown provenance. Showing
 * a single "Absent" badge for both would hide precisely the fact an operator
 * needs.
 *
 * This surface is DIAGNOSTIC. It renders no action control, and Gate D
 * deliberately adds none.
 */

export const FAMILY_EVIDENCE_PRESENTATION = {
  satisfied: {
    label: "Satisfied",
    className: "bg-emerald-500/20 text-emerald-300",
    explanation: "The desired governed CRM state is already present.",
  },
  superseded: {
    label: "Superseded",
    className: "bg-sky-500/20 text-sky-300",
    explanation: "Newer governed CRM evidence is present.",
  },
  absent_no_evidence: {
    label: "Absent — No Evidence",
    className: "bg-slate-500/20 text-slate-300",
    explanation: "No current governed CRM evidence was observed for this field family.",
  },
  absent_divergent: {
    label: "Absent — Divergent",
    className: "bg-orange-500/20 text-orange-300",
    explanation:
      "Governed CRM evidence exists, but it differs from the historical desired state. Automatic convergence must not overwrite it.",
  },
  absent_undecidable: {
    label: "Absent — Undecidable",
    className: "bg-amber-500/20 text-amber-300",
    explanation:
      "The persisted verification evidence is insufficient to determine whether this family was empty or changed.",
  },
  conflicted: {
    label: "Conflicted",
    className: "bg-rose-500/20 text-rose-300",
    explanation: "Provider state could not be compared for this field family.",
  },
  unknown: {
    label: "Unknown",
    className: "bg-slate-500/20 text-slate-400",
    explanation: "This build does not recognize the persisted disposition value.",
  },
};

export const RECORD_SAFETY_SHAPE_PRESENTATION = {
  all_absent_no_evidence: {
    label: "All Absent — No Evidence",
    className: "bg-slate-500/20 text-slate-300",
    explanation:
      "No governed CRM evidence was observed for any field family in this reconciliation.",
  },
  mixed_safe: {
    label: "Mixed — No Divergence",
    className: "bg-slate-500/20 text-slate-300",
    explanation:
      "Some families are already satisfied or superseded; the rest hold no governed CRM evidence. Nothing observed would be overwritten.",
  },
  has_absent_divergent: {
    label: "Divergent Evidence Present",
    className: "bg-orange-500/20 text-orange-300",
    explanation:
      "At least one field family holds governed CRM evidence that differs from the historical desired state.",
  },
  has_conflict: {
    label: "Conflicted",
    className: "bg-rose-500/20 text-rose-300",
    explanation: "At least one field family could not be compared against provider state.",
  },
  has_undecidable_family: {
    label: "Undecidable Family Present",
    className: "bg-amber-500/20 text-amber-300",
    explanation:
      "At least one field family cannot be decomposed from the persisted verification evidence.",
  },
  identity_anomaly: {
    label: "Identity Anomaly",
    className: "bg-rose-500/20 text-rose-300",
    explanation:
      "The currently matched contacts differ from those the historical projection acted on, so the field comparisons do not describe the same target.",
  },
  other: {
    label: "Nothing Owed",
    className: "bg-slate-500/20 text-slate-400",
    explanation: "No field family in this reconciliation is outstanding.",
  },
};

export const IDENTITY_RELATIONSHIP_LABELS = {
  exact_set_match: "Exact match",
  multi_contact_exact_match: "Exact match (multiple contacts)",
  identity_drift: "Identity drift",
  contact_missing_now: "Contact missing now",
  unknown: "Unknown",
};

/**
 * Returns the presentation entry for a backend-derived family evidence value,
 * or `null` when there is none to show. Never invents a label for an
 * unrecognized value — an unknown value renders as absent rather than
 * mislabelled, matching emailHygieneRecoveryState.js's own contract.
 */
export function familyEvidencePresentation(value) {
  if (!value) return null;
  return FAMILY_EVIDENCE_PRESENTATION[value] || null;
}

export function recordSafetyShapePresentation(value) {
  if (!value) return null;
  return RECORD_SAFETY_SHAPE_PRESENTATION[value] || null;
}

export function identityRelationshipLabel(value) {
  if (!value) return null;
  return IDENTITY_RELATIONSHIP_LABELS[value] || null;
}

/** Governed field-family keys rendered in a stable, operator-meaningful order. */
export const FAMILY_LABELS = {
  validation: "Validation",
  delivery: "Delivery",
  email_dnd_required: "Email DND Required",
  suppression_reason: "Suppression Reason",
};

export function familyLabel(family) {
  return FAMILY_LABELS[family] || family;
}
